import React, { useState, useEffect } from "react";
import { LOGO_URL } from "../../content";
import { Menu, X } from "lucide-react";

export default function Nav({ t, lang, setLang }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "heritage", label: t.nav.heritage },
    { id: "odoc", label: t.nav.odoc },
    { id: "menu", label: t.nav.menu },
    { id: "model", label: t.nav.model },
    { id: "financials", label: t.nav.financials },
    { id: "premium", label: t.nav.premium },
    { id: "faq", label: t.nav.faq },
  ];

  const handleNavClick = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <a href="#top" onClick={(e) => handleNavClick(e, "top")} className="flex items-center gap-3" data-testid="brand-logo">
          <img src={LOGO_URL} alt="Hari Ram Purwar" className="h-12 w-12 rounded-full object-cover ring-2 ring-amber-600/40" />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-stone-50 text-xl tracking-wide">Hari Ram Purwar</div>
            <div className="text-amber-500 text-[10px] uppercase tracking-[0.25em]">Since 1890</div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7 text-sm text-stone-300">
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={(e) => handleNavClick(e, l.id)}
              data-testid={`nav-link-${l.id}`}
              className="hover:text-amber-500 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="inline-flex items-center bg-stone-900 border border-stone-800 rounded-full p-1" data-testid="lang-toggle">
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1 text-xs uppercase tracking-wider rounded-full transition-all ${
                lang === "en" ? "bg-amber-600 text-stone-950 font-medium" : "text-stone-400 hover:text-stone-100"
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLang("hi")}
              data-testid="lang-hi"
              className={`px-3 py-1 text-xs tracking-wider rounded-full transition-all ${
                lang === "hi" ? "bg-amber-600 text-stone-950 font-medium" : "text-stone-400 hover:text-stone-100"
              }`}
            >
              हिं
            </button>
          </div>

          <a
            href="#apply"
            onClick={(e) => handleNavClick(e, "apply")}
            data-testid="nav-apply-btn"
            className="hidden md:inline-flex items-center bg-amber-600 hover:bg-amber-700 text-stone-950 font-medium uppercase tracking-wider text-xs px-5 py-2.5 rounded-full btn-primary-glow"
          >
            {t.nav.apply}
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-stone-200 p-2"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden glass-nav mt-3 border-t border-stone-800" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => handleNavClick(e, l.id)}
                className="text-stone-300 hover:text-amber-500 py-2 text-sm"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#apply"
              onClick={(e) => handleNavClick(e, "apply")}
              className="bg-amber-600 hover:bg-amber-700 text-stone-950 font-medium uppercase tracking-wider text-xs px-5 py-3 rounded-full text-center mt-2"
            >
              {t.nav.apply}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
